/**
 * ForgotPassword.jsx - Password Reset Request Page
 * 
 * @description
 * Page for users to request a password reset by entering their email address.
 * Sends a reset link to the user's email address.
 * 
 * @route /forgot-password
 * @auth Not required (public page)
 * @redirects /signin with success message after email sent
 * 
 * @formFields
 * - email: User email address for password reset
 * 
 * @features
 * - Email validation with error messages
 * - Loading states during submission
 * - Toast notifications for feedback
 * - Dark theme support
 * - Mobile responsive design
 * - Link back to sign in page
 * 
 * @validation
 * - Email: Required, valid email format
 * - Real-time validation on blur
 * - Clear errors on field change
 * 
 * @stateManagement
 * - email: Email input value
 * - errors: Validation error messages
 * - isLoading: Submission state
 * - isSubmitted: Success state after email sent
 * 
 * @passwordReset
 * - Sends reset request to backend API
 * - Handles validation and error responses
 * - Shows success message on completion
 * 
 * @styling
 * - Consistent with SignIn/SignUp pages
 * - Gradient background and form styling
 * - Focus states and animations
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LoadingButton } from '../components/LoadingSpinner';
import { useToast } from '../components/Toast';
import { sendPasswordResetEmail } from '../services/emailService.js';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, ToastContainer } = useToast();
  
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    // Clear error when user starts typing
    if (errors.email) {
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const handleEmailBlur = () => {
    if (email && !validateEmail(email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('Please fix the errors in the form');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Send password reset email using email service
      await sendPasswordResetEmail(email);
      
      setIsSubmitted(true);
      showSuccess('Password reset link sent! Check your email.');
      
      // Redirect to sign in page after a delay
      setTimeout(() => {
        navigate('/signin', { 
          state: { 
            message: 'Password reset link sent to your email. Please check your inbox.' 
          }
        });
      }, 3000);
      
    } catch (error) {
      showError(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800">
        <ToastContainer />
        
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mb-6">
              <svg className="h-8 w-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
              Check Your Email
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              We've sent a password reset link to
            </p>
            <p className="text-sm font-medium text-purple-600 dark:text-purple-400">
              {email}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl">
            <div className="space-y-4 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Click the link in your email to reset your password. The link will expire in 1 hour.
              </p>
              
              <div className="pt-4">
                <p className="text-xs text-gray-500 dark:text-gray-500 mb-3">
                  Didn't receive the email? Check your spam folder or
                </p>
                <button
                  onClick={() => {
                    setIsSubmitted(false);
                    setEmail('');
                  }}
                  className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-500 font-medium"
                >
                  Try again with a different email
                </button>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link to="/signin" className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
              ← Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800">
      <ToastContainer />
      
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/" className="flex items-center justify-center mb-2">
            <img src="/logo.svg" alt="Link Spaghetti Logo" className="h-10" />
          </Link>
          <Link to="/" className="inline-block mb-4">
            <h1 className="text-3xl font-bold text-transparent bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text">
              Link Spaghetti
            </h1>
          </Link>
          
          <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
            Forgot your password?
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl">
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  className={`appearance-none block w-full px-3 py-2 border ${
                    errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  } rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
                  placeholder="Enter your email address"
                  disabled={isLoading}
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.email}</p>
                )}
              </div>
            </div>

            <div className="mt-6">
              <LoadingButton
                type="submit"
                loading={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 focus:ring-offset-white dark:focus:ring-offset-gray-800"
                disabled={isLoading}
              >
                Send Reset Link
              </LoadingButton>
            </div>

            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Remember your password?{' '}
                <Link to="/signin" className="text-purple-600 dark:text-purple-400 hover:text-purple-500 font-medium">
                  Sign in instead
                </Link>
              </p>
            </div>
          </div>
        </form>

        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Need help?{' '}
            <Link to="/" className="text-purple-600 dark:text-purple-400 hover:text-purple-500">
              Contact Support
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
